import { Alert, Box, List, ListItem, Stack, Typography } from "@mui/material";
import { useFeed } from "@bw/shared/hooks";
import { FeedSkeleton } from "../../social/components/FeedSkeleton";
import { MaterialSocialPostCard } from "../../../common/components/MaterialSocialPostCard";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

const GALLERY_CAP = 6;

export function PlayerPostGallery({ userId }: { userId: string }) {
  const feedQ = useFeed({ scope: "user", userId, limit: GALLERY_CAP });
  return (
    <Box component="section" aria-labelledby="player-gallery-title">
      <Stack spacing={1.5}>
        <Typography id="player-gallery-title" component="h2" variant="h6">
          Posts
        </Typography>
        <PlayerFeed feedQ={feedQ} />
      </Stack>
    </Box>
  );
}

function PlayerFeed({ feedQ }: { feedQ: ReturnType<typeof useFeed> }) {
  if (feedQ.isLoading) return <FeedSkeleton />;
  if (feedQ.isError) {
    // A look-only minor (#2691) is told why posts wait, not "couldn't load".
    return (
      <GuardianAwareError error={feedQ.error}>
        <Alert severity="error" role="alert">
          We couldn&apos;t load these posts. Please try again.
        </Alert>
      </GuardianAwareError>
    );
  }
  // The subject endpoint is requested with the six-post cap, but keep the
  // preview bounded even if a malformed response returns more than requested.
  const posts = feedQ.posts.slice(0, GALLERY_CAP);
  if (posts.length === 0)
    return <Typography color="text.secondary">No posts yet.</Typography>;
  return (
    <List disablePadding sx={{ display: "grid", gap: 1.5 }}>
      {posts.map((post) => (
        <ListItem key={post.post_id} disablePadding>
          <MaterialSocialPostCard
            post={post}
            detailPath={(id) => `/community/posts/${id}`}
            profilePath={(uid) => `/players/${uid}`}
          />
        </ListItem>
      ))}
    </List>
  );
}
